

import { Component, Prop, Watch } from "vue-property-decorator";
import {
  BackupId,
  BackupRepositoryInterface,
  BackupRestoreType,
  RestoreBackupDto,
  WebsiteBackupId,
  WebsiteBackupSummaryDto, WebsiteGroupRestoreBackupDto, WebsiteGroupRestoreBackupDtoExtended, WebsiteRestoreBackupDto,
} from "@/services/Backup/interfaces";
import { DataTableHeader } from "vuetify";
import PaginationControl from "@/components/backups/PaginationControl.vue";
import Themeable from "vuetify/lib/mixins/themeable";
import BackupDetailsDialog from "@/components/dialogs/backups/BackupDetailsDialog.vue";
import RestoreBackupDialog from "@/components/dialogs/backups/RestoreBackupDialog.vue";
import DeleteBackupDialog from "@/components/dialogs/backups/DeleteBackupDialog.vue";
import { AxiosError } from "axios";
import WebsiteRepositoryInterface, { Website } from "@/services/Website/interfaces";
import WebsiteGroupRepositoryInterface, { WebsiteGroupItem } from "@/services/WebsiteGroup/interfaces";

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    PaginationControl,
    BackupDetailsDialog,
    RestoreBackupDialog,
    DeleteBackupDialog,
  },
  inject: [
    'backupRepository',
    'websiteRepository',
    'websiteGroupRepository',
  ],
})
export default class WebsiteBackupsTable {
  @Prop() private readonly backupId!: number;
  @Prop() private readonly websiteGroupId?: number;
  @Prop() private readonly backupScope!: string;
  @Prop() private readonly originServer: { originServerHostname: string, originServerId: string };

  private readonly backupRepository!: BackupRepositoryInterface;
  private websiteRepository!: WebsiteRepositoryInterface;
  private websiteGroupRepository!: WebsiteGroupRepositoryInterface;

  private websiteBackups: WebsiteBackupSummaryDto[] = [];
  private page: number = 1;
  private itemsPerPage: number = 10;
  private totalItems: number = 0;
  private loading: boolean = false;

  private isConfigurationVisible: boolean = false;
  private selectedWebsite: WebsiteBackupSummaryDto | null = null;

  private _searchWebsiteId: number = 0;
  private _websiteSearch: string = '';
  private websiteSearchLoading: boolean = true;
  private websites: WebsiteBackupSummaryDto[] = [];

  private _searchGroupId: [number, number] = [0, 0];
  private _websiteGroupSearch: string = '';
  private websiteGroupSearchLoading: boolean = true;
  private websiteGroups: WebsiteBackupSummaryDto[] = [];

  public showFilterWebsiteGroupRestore = false;

  public headers: DataTableHeader[] = [];

  public async fetchWebsiteBackups(): Promise<void> {
    this.loading = true;

    try {
      const result = await this.backupRepository.getWebsiteBackups(
        this.backupId,
        (this._searchWebsiteId ?? 0) > 0 ? this._searchWebsiteId : undefined,
        undefined,
        (this._searchGroupId?.[0] ?? 0) > 0 ? this._searchGroupId[0] : undefined,
        {
          page: this.page,
          pageSize: this.itemsPerPage,
        });

      this.totalItems = result.total;
      this.websiteBackups = result.data;
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loading = false;
    }
  }

  public get websiteSearch(): string {
    return this._websiteSearch;
  }

  public set websiteSearch(val: string) {
    if (val && val.length < 3) {
      return;
    }

    this._websiteSearch = `${val}`;
    this.websiteSearchLoading = true;

    this.backupRepository.getWebsiteBackups(
      this.backupId,
      undefined,
      val,
      undefined,
      undefined,
    ).then((websites) => {
      this.websites = websites.data;
      this.websiteSearchLoading = false;
    }).catch((err) => {
      this.$errorDialog.show(err.message);
    });
  }

  get searchWebsiteId(): number {
    return this._searchWebsiteId;
  }

  set searchWebsiteId(value: number) {
    this._searchWebsiteId = value;
    this.page = 1;
    if (value) {
      this._searchGroupId = [0, 0];
      this._websiteGroupSearch = "";
      this.showFilterWebsiteGroupRestore = false;

      this.$refs.searchWebsiteGroup.reset();
    }

    this.fetchWebsiteBackups();
  }

  public get websiteGroupSearch(): string {
    return this._websiteGroupSearch;
  }

  public set websiteGroupSearch(val: string) {
    if (val && val.length < 3) {
      return;
    }

    this._websiteGroupSearch = `${val}`;
    this.websiteGroupSearchLoading = true;

    this.backupRepository.getWebsiteBackups(this.backupId,
      undefined,
      val,
      undefined,
      undefined,
    ).then((websites) => {
      this.websiteGroups = websites.data;
      this.websiteGroupSearchLoading = false;
    }).catch((err) => {
      this.$errorDialog.show(err.message);
    });
  }

  get searchGroupId(): string {
    return this._searchGroupId?.join("_") as string;
  }

  set searchGroupId(value: string) {
    this._searchGroupId = value?.split("_").map(x => parseInt(x)) as [number, number] ?? [0, 0];
    this.page = 1;
    if (value) {
      this._searchWebsiteId = 0;
      this._websiteSearch = "";
      this.showFilterWebsiteGroupRestore = true;

      this.$refs.searchWebsite.reset();
    } else {
      this.showFilterWebsiteGroupRestore = false;
    }

    this.fetchWebsiteBackups();
  }

  public groupId(websiteBackup: WebsiteBackupSummaryDto): string {
    return websiteBackup.websiteGroupId + "_" + websiteBackup.websiteId;
  }

  @Watch('backupId', { immediate: true })
  onBackupIdChange(newBackupId: BackupId) {
    this.fetchWebsiteBackups();
  }

  public async created(): Promise<void> {
    this.headers = [
      { text: 'ID', value: 'id' },
      { text: 'Website', value: 'websiteId' },
      { text: 'Status', value: 'status' },
      { text: 'Configuration', value: 'config' },
    ];
    if (this.backupScope !== 'account') {
      this.headers.push({ text: 'Actions', value: 'actions', sortable: false });
    }

    await this.fetchWebsiteBackups();
  }

  public async changePage(page: number) {
    this.page = page;
    await this.fetchWebsiteBackups();
  }

  public async showConfiguration(backupId: number, websiteId: number) {
    this.selectedWebsite = await this.backupRepository.getWebsiteBackup(backupId, websiteId)
      .then(x => {
        let tmpJson = JSON.parse(x.configJson ?? "{}");
        for (let key of Object.keys(tmpJson)) {
          tmpJson[key] = JSON.parse(tmpJson[key] ?? "{}");
        }
        x.configJson = JSON.stringify(tmpJson, null, 4);
        return x;
      });
    this.isConfigurationVisible = true;
  }

  public hideConfiguration() {
    this.selectedWebsite = null;
    this.isConfigurationVisible = false;
  }

  public restoreWebsiteBackup(websiteBackupId: number, config: string, targets: unknown[]): void {
    const data: WebsiteRestoreBackupDto = {
      websiteBackupId: websiteBackupId,
      targetBackupId: this.backupId,
      config: JSON.parse(config),
      targets: targets as any,
    };

    this.$emit('restore', data);
  }

  public restoreWebsiteGroupBackup(websiteGroupId: number): void {
    const data: WebsiteGroupRestoreBackupDtoExtended = {
      websiteGroupId: websiteGroupId,
      targetBackupId: this.backupId,
      scope: this.backupScope,
      mainWebsiteDomain: this.websiteBackups.filter(x => x.isMain).pop()?.domain ?? websiteGroupId.toString()
    };

    this.$emit('restoreGroup', data);
  }

  private handleError(err: unknown): void {
    const asAxiosError = err as AxiosError;
    if (asAxiosError.isAxiosError) {
      this.$errorDialog.show(asAxiosError.response?.data?.message ?? 'An axios error occurred');
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    } else {
      this.$errorDialog.show('An unknown error occurred');
    }
  }
}
